import React, { useEffect, useRef } from "react";
// ANT DESIGN COMPONENTS
import { Button, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// TIMEZONE
import moment from "moment-timezone";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faDownload } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateOpenDrawerFilters,
  updateFilters,
  updateOpenAlertDownloadReport,
  updateChartReportTheme,
} from "../../redux/reports/reportsSlice";
// COMPONENTS
import ReportsFilterDateRange from "./filters/ReportsFilterDateRange";
import ReportsFilterDateType from "./filters/ReportsFilterDateType";
import ReportsFilterLevelType from "./filters/ReportsFilterLevelType";
import ReportsFilterGetBanks from "./filters/ReportsFilterGetBanks";
import ReportsFilterGetServers from "./filters/ReportsFilterGetServers";
import ReportsFilterGetOrganizations from "./filters/ReportsFilterGetOrganizations";
import ReportsFilterGetLifts from "./filters/ReportsFilterGetLifts";
import ReportsFilterDateDay from "./filters/ReportsFilterDateDay";
import ReportsFilterDateWeek from "./filters/ReportsFilterDateWeek";
import ReportsFilterDateMonth from "./filters/ReportsFilterDateMonth";
import ReportsFilterDateQuarter from "./filters/ReportsFilterDateQuarter";
import ReportsFilterDateYear from "./filters/ReportsFilterDateYear";
import ReportsFilterWeeksNumber from "./filters/ReportsFilterWeeksNumber";
import ReportsFilterLimitNumber from "./filters/ReportsFilterLimitNumber";
import ReportsPDFViewerModal from "./ReportsPDFViewerModal";
import ReportsDrawerFilters from "./drawer/ReportsDrawerFilters";
// UTILS
import { getLocalTimezone } from "../../utils/timezone";
// INTERFACES
import { RootState } from "../../app/store";
import {
  ReportsHeaderInterface,
  ReportSliceFiltersInterface,
} from "../../interfaces/Reports.interface";

function ReportsHeader({ reportDefaultData }: ReportsHeaderInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters } = useSelector((state: RootState) => state.report);
  const { theme, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getDefaultTimezone = () => {
    // Find default timezone. getLocalTimezone always resolves to a real entry —
    // an empty timezoneCode would silently block every report fetch, since the
    // charts and the table gate on filters.timezoneCode !== "".
    const localTimezone = getLocalTimezone();
    return {
      timezoneName: localTimezone.name,
      timezoneLabel: localTimezone.label,
      timezoneCode: localTimezone.tzCode,
      timezoneUTC: localTimezone.utc,
    };
  };

  const getDefaultStartDate = (dateType: string) => {
    if (dateType === "TODAY") {
      return moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "WEEK") {
      const startWeekNumber = moment().week();
      const year = moment().year();
      const date1 = moment().year(year).week(startWeekNumber).startOf("week");
      return moment(date1).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "MONTH") {
      return moment().startOf("month").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "QUARTER") {
      const currentQuarter = moment().quarter();
      let firstMonthOfQuarter: any;
      // eslint-disable-next-line default-case
      switch (currentQuarter) {
        case 1: // Q1
          firstMonthOfQuarter = "1"; // January
          break;
        case 2: // Q2
          firstMonthOfQuarter = "4"; // April
          break;
        case 3: // Q3
          firstMonthOfQuarter = "7"; // July
          break;
        case 4: // Q4
          firstMonthOfQuarter = "10"; // October
          break;
      }
      return moment()
        .month(firstMonthOfQuarter - 1)
        .startOf("month")
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "YEAR") {
      return moment().startOf("year").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    return "";
  };

  const getDefaultEndDate = (dateType: string) => {
    if (dateType === "TODAY") {
      return moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "WEEK") {
      const endWeekNumber = moment().week();
      const year = moment().year();
      const date2 = moment().year(year).week(endWeekNumber).endOf("week");
      return moment(date2).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "MONTH") {
      return moment().endOf("month").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "QUARTER") {
      const currentQuarter = moment().quarter();
      let lastMonthOfQuarter: any;
      // eslint-disable-next-line default-case
      switch (currentQuarter) {
        case 1: // Q1
          lastMonthOfQuarter = "3"; // March
          break;
        case 2: // Q2
          lastMonthOfQuarter = "6"; // June
          break;
        case 3: // Q3
          lastMonthOfQuarter = "9"; // September
          break;
        case 4: // Q4
          lastMonthOfQuarter = "12"; // December
          break;
      }
      return moment()
        .month(lastMonthOfQuarter - 1)
        .endOf("month")
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    if (dateType === "YEAR") {
      return moment().endOf("year").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    }
    return "";
  };

  const onClickResetFilters = () => {
    const defaultTime = getDefaultTimezone();
    // Format the current date to the specified format
    dispatch(
      updateFilters({
        reportType: reportDefaultData.reportType,
        levelTypeName: reportDefaultData.levelTypeName,
        displaySelectTypeLevel: reportDefaultData.displaySelectTypeLevel,
        liftType: reportDefaultData.liftType,
        startDate: getDefaultStartDate(reportDefaultData.dateType),
        endDate: getDefaultEndDate(reportDefaultData.dateType),
        dateType: reportDefaultData.dateType,
        organizationId: "",
        organizationName: "",
        serverName: "",
        serverId: "",
        liftName: "",
        liftId: "",
        bankName: "",
        bankId: "",
        banks: [],
        allowAllBanks: Boolean(reportDefaultData.allowAllBanks),
        displayAdvanceFiltersExclusions: reportDefaultData.displayAdvanceFiltersExclusions,
        exclusionTimeList: [],
        exclusionDateList: [],
        exclusionTime: false,
        exclusionDate: false,
        exclusionSaturdays: false,
        exclusionSundays: false,
        exclusionHolidays: false,
        timezoneCode: defaultTime?.timezoneCode,
        timezoneLabel: defaultTime?.timezoneLabel,
        timezoneUTC: defaultTime?.timezoneUTC,
        timezoneName: defaultTime?.timezoneName,
        timezoneLocal: false,
        tableListColumns: [],
        tableSelectedColumns: [],
        useTablePagination: reportDefaultData.useTablePagination,
        defaultColumns: reportDefaultData.defaultColumns,
        displayAdvanceFiltersOffsets: reportDefaultData.displayAdvanceFiltersOffsets,
        disabledOffsets: reportDefaultData.disabledOffsets,
        defaultOffsets: reportDefaultData.defaultOffsets,
        offsetsList: reportDefaultData.defaultOffsets,
        infoDescriptionText: reportDefaultData.infoDescriptionText,
        reportTitle: reportDefaultData.reportTitle,
        displayDownloadCSVReport: reportDefaultData.displayDownloadCSVReport,
        displayDownloadPDFReport: reportDefaultData.displayDownloadPDFReport,
        displayRecentWeeksPicket: reportDefaultData.displayRecentWeeksPicket,
        displayInputLimitItems: reportDefaultData.displayInputLimitItems,
        weeksNumber: 5,
        limitNumber: 10,
        sortAsc: true,
        getFirstDefaultValue: true,
        carsList: [],
        selectedCarsList: [],
        chartType: reportDefaultData.chartType,
        displayAdvanceFilterCharts: reportDefaultData.displayAdvanceFilterCharts,
        displayAdvanceFiltersTable: reportDefaultData.displayAdvanceFiltersTable,
        displayDateByRange: reportDefaultData.displayDateByRange,
        displayMinimumActivation: reportDefaultData.displayMinimumActivation,
        displayDate: reportDefaultData.displayDate,
        displaySelectLiftType: reportDefaultData.displaySelectLiftType,
        dateTypeOptions: reportDefaultData.dateTypeOptions,
        dateDaysLimit: reportDefaultData.dateDaysLimit,
        dateWeeksLimit: reportDefaultData.dateWeeksLimit,
        dateMonthsLimit: reportDefaultData.dateMonthsLimit,
        dateQuarterLimit: reportDefaultData.dateQuarterLimit,
        dateYearsLimit: reportDefaultData.dateYearsLimit,
        dateTimeSeconds: 0,
        displayChartMirror: reportDefaultData.displayChartMirror,
        chartMirror: reportDefaultData.chartMirror,
        urlChartImage: "",
      }),
    );
  };

  const onClickDownloadCSVAndPDF = () => {
    dispatch(updateOpenAlertDownloadReport(true));
  };

  const disabledSearchReport = () => {
    let disabled = false;
    if (
      (filters.levelTypeName === "ORGANIZATIONS" && filters.organizationId === "") ||
      (filters.levelTypeName === "SERVERS" && filters.serverId === "") ||
      (filters.levelTypeName === "BANKS" && filters.bankId === "") ||
      (filters.levelTypeName === "LIFTS" && filters.liftId === "")
    ) {
      disabled = true;
    }
    return disabled;
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    onClickResetFilters();
  }, []);

  useEffect(() => {
    dispatch(updateChartReportTheme(theme));
  }, [theme]);

  // When the global org changes in the header, reset downstream report filters.
  // Skip initial mount — onClickResetFilters already handles the initial reset.
  const prevOrgRef = useRef(globalOrganizationId);
  useEffect(() => {
    if (prevOrgRef.current === globalOrganizationId) {
      return;
    }
    prevOrgRef.current = globalOrganizationId;
    if (globalOrganizationId) {
      const copy: ReportSliceFiltersInterface = {
        ...filters,
        organizationId: "",
        organizationName: "",
        serverId: "",
        serverName: "",
        bankId: "",
        bankName: "",
        liftId: "",
        liftName: "",
        getFirstDefaultValue: true,
      };
      dispatch(updateFilters(copy));
    }
  }, [globalOrganizationId]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__spaceBetween" style={{ flexWrap: "wrap" }}>
      <div className="generalStyles__flex mt-1" style={{ flexWrap: "wrap" }}>
        {/**  DATE */}
        {filters.displayDate && (
          <>
            {/**  DATE TYPE */}
            <div className="generalStyles__mrFix generalStyles__width200px">
              <ReportsFilterDateType />
            </div>
            {filters.displayDateByRange ? (
              <>
                <div className="generalStyles__mrFix generalStyles__width200px">
                  <ReportsFilterDateRange />
                </div>
              </>
            ) : (
              <div className="generalStyles__mrFix generalStyles__width200px">
                {filters.dateType === "TODAY" && <ReportsFilterDateDay />}
                {filters.dateType === "WEEK" && <ReportsFilterDateWeek />}
                {filters.dateType === "MONTH" && <ReportsFilterDateMonth />}
                {filters.dateType === "QUARTER" && <ReportsFilterDateQuarter />}
                {filters.dateType === "YEAR" && <ReportsFilterDateYear />}
              </div>
            )}
          </>
        )}
        {/** LIMIT ITEMS */}
        {filters.displayInputLimitItems && (
          <div className="generalStyles__width150px generalStyles__mrFix">
            <ReportsFilterLimitNumber />
          </div>
        )}
        {/**  WEEKS PICKER */}
        {filters.displayRecentWeeksPicket && (
          <div className="generalStyles__width150px generalStyles__mrFix">
            <ReportsFilterWeeksNumber />
          </div>
        )}
        {/** CASCADER LEVEL TYPE */}
        {filters.displaySelectTypeLevel && (
          <div className="generalStyles__mrFix generalStyles__width200px">
            <ReportsFilterLevelType />
          </div>
        )}
        {/** FILTER BY CASCADER */}
        <div className="generalStyles__mrFix generalStyles__width400px">
          <div>
            {filters.levelTypeName === "SERVERS" && <ReportsFilterGetServers />}
            {filters.levelTypeName === "BANKS" && <ReportsFilterGetBanks />}
            {filters.levelTypeName === "ORGANIZATIONS" && <ReportsFilterGetOrganizations />}
            {filters.levelTypeName === "LIFTS" && <ReportsFilterGetLifts />}
          </div>
        </div>
      </div>
      <div className="generalStyles__flex">
        {/** DOWNLOAD CSV/EXCEL/ PDF */}
        {(filters.displayDownloadCSVReport || filters.displayDownloadPDFReport) && (
          <div className="generalStyles__mlFix">
            <Tooltip placement="topLeft" title={`${t("general.download")} ${t("general.report")}`}>
              <Button
                className={
                  disabledSearchReport() ? "buttonStyle__inactive__1__dark" : "buttonStyle__8"
                }
                icon={<FontAwesomeIcon icon={faDownload} />}
                onClick={onClickDownloadCSVAndPDF}
                disabled={disabledSearchReport()}
              />
            </Tooltip>
          </div>
        )}
        {/** OPEN ADVANCE FILTERS DRAWER */}
        <div className="generalStyles__mlFix">
          <Tooltip placement="topLeft" title={t("general.advanceFilters")}>
            <Button
              className="buttonStyle__6"
              icon={<FontAwesomeIcon icon={faFilter} />}
              onClick={() => {
                dispatch(updateOpenDrawerFilters(true));
              }}
            />
          </Tooltip>
        </div>
      </div>
      {/** COMPONENTES */}
      <ReportsDrawerFilters />
      <ReportsPDFViewerModal />
    </div>
  );
}

export default ReportsHeader;
